import React, { useState, useEffect, useMemo } from 'react';
import { User, Team, usersAPI, authAPI, teamsAPI } from '../../services/api';
import {
  PlusIcon,
  UserIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  UserPlusIcon,
  EnvelopeIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  LanguageIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, CardHeader, Button, Badge, Spinner, Modal, EmptyState, PageHero, HelpTooltip, Tabs } from '../ui';

type TabType = 'all' | 'admins' | 'salesmen' | 'teams';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Teams State
  const [showCreateTeamForm, setShowCreateTeamForm] = useState(false);
  const [showEditTeamForm, setShowEditTeamForm] = useState(false);
  const [showDeleteTeamDialog, setShowDeleteTeamDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [showTeamDetails, setShowTeamDetails] = useState(false);

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);
  const [resetPasswordStrength, setResetPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'USER' as 'USER' | 'ADMIN',
    preferredLanguage: 'ARABIC' as 'ARABIC' | 'ENGLISH',
    teamId: '' as string | number,
  });
  const [editFormData, setEditFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'USER' as 'USER' | 'ADMIN',
    isActive: true,
    preferredLanguage: 'ARABIC' as 'ARABIC' | 'ENGLISH',
    teamId: '' as string | number,
  });

  const [resetPasswordFormData, setResetPasswordFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  const [teamFormData, setTeamFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersData, teamsData] = await Promise.all([
        usersAPI.getAll(),
        teamsAPI.getAll(),
      ]);
      setUsers(usersData);
      setTeams(teamsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      const { teamId, ...userPayload } = formData;

      await authAPI.register({
        ...userPayload,
        ...(teamId ? { teamId: Number(teamId) } : {}),
      });

      setSuccess('User created successfully!');
      setShowCreateForm(false);
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'USER',
        preferredLanguage: 'ARABIC',
        teamId: '',
      });
      fetchData(); // Refresh the user list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  const handleCreateTeamSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      await teamsAPI.create(teamFormData);

      setSuccess('Team created successfully!');
      setShowCreateTeamForm(false);
      setTeamFormData({ name: '', description: '', isActive: true });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create team');
    }
  };

  const checkPasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return 'weak';
    if (strength <= 3) return 'medium';
    return 'strong';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Check password strength
    if (name === 'password') {
      if (value.length > 0) {
        setPasswordStrength(checkPasswordStrength(value));
      } else {
        setPasswordStrength(null);
      }
    }
  };

  const handleResetPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setResetPasswordFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'password') {
      setResetPasswordStrength(value.length > 0 ? checkPasswordStrength(value) : null);
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      email: user.email,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role,
      isActive: user.isActive ?? true,
      preferredLanguage: (user as any).preferredLanguage || 'ARABIC',
      teamId: user.teamId || '',
    });
    setShowEditForm(true);
    setError('');
    setSuccess('');
  };

  const handleResetPasswordUser = (user: User) => {
    setSelectedUser(user);
    setResetPasswordFormData({ password: '', confirmPassword: '' });
    setResetPasswordStrength(null);
    setShowResetPassword(false);
    setShowResetPasswordForm(true);
    setError('');
    setSuccess('');
  };

  const closeResetPasswordForm = () => {
    setShowResetPasswordForm(false);
    setSelectedUser(null);
    setResetPasswordFormData({ password: '', confirmPassword: '' });
    setResetPasswordStrength(null);
    setShowResetPassword(false);
  };

  const handleEditTeam = (team: Team) => {
    setSelectedTeam(team);
    setTeamFormData({
      name: team.name,
      description: team.description || '',
      isActive: team.isActive,
    });
    setShowEditTeamForm(true);
    setError('');
    setSuccess('');
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      setError('');
      setSuccess('');
      await usersAPI.update(selectedUser.id, {
        ...editFormData,
        teamId: editFormData.teamId ? Number(editFormData.teamId) : null,
      });

      setSuccess('User updated successfully!');
      setShowEditForm(false);
      setSelectedUser(null);
      fetchData(); // Refresh the user list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    if (resetPasswordFormData.password !== resetPasswordFormData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setError('');
      setSuccess('');
      await usersAPI.resetPassword(selectedUser.id, resetPasswordFormData.password);
      setSuccess(`Password reset successfully for ${selectedUser.firstName || selectedUser.email}.`);
      closeResetPasswordForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleEditTeamSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeam) return;

    try {
      setError('');
      setSuccess('');

      await teamsAPI.update(selectedTeam.id, teamFormData);

      setSuccess('Team updated successfully!');
      setShowEditTeamForm(false);
      setSelectedTeam(null);
      fetchData(); // Refresh list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update team');
    }
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
    setError('');
    setSuccess('');
  };

  const handleDeleteTeam = (team: Team) => {
    setSelectedTeam(team);
    setShowDeleteTeamDialog(true);
    setError('');
    setSuccess('');
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setError('');
      setSuccess('');

      await usersAPI.delete(selectedUser.id);

      setSuccess('User deleted successfully!');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchData(); // Refresh the user list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
      setShowDeleteDialog(false);
      setSelectedUser(null);
    }
  };

  const confirmDeleteTeam = async () => {
    if (!selectedTeam) return;

    try {
      setError('');
      setSuccess('');

      await teamsAPI.delete(selectedTeam.id);

      setSuccess('Team deleted successfully!');
      setShowDeleteTeamDialog(false);
      setSelectedTeam(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete team');
      setShowDeleteTeamDialog(false);
      setSelectedTeam(null);
    }
  };

  const assignUserToTeam = async (userId: number, teamId: number) => {
    try {
      await teamsAPI.addMember(teamId, userId);
      setSuccess('Salesman added to team');
      fetchData();

      // Update selectedTeam users if we are in team details view
      if (selectedTeam && selectedTeam.id === teamId) {
        const teamData = await teamsAPI.getById(teamId);
        setSelectedTeam(teamData);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add member to team');
    }
  };

  const removeUserFromTeam = async (userId: number, teamId: number) => {
    try {
      await teamsAPI.removeMember(teamId, userId);
      setSuccess('Salesman removed from team');
      fetchData();

      // Update selectedTeam users if we are in team details view
      if (selectedTeam && selectedTeam.id === teamId) {
        const teamData = await teamsAPI.getById(teamId);
        setSelectedTeam(teamData);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove member from team');
    }
  };

  const getRoleBadge = (role: string) => {
    return role === 'ADMIN' ? 'secondary' : 'primary';
  };

  // Filter users based on active tab
  const filteredUsers = useMemo(() => {
    if (activeTab === 'admins') {
      return users.filter(user => user.role === 'ADMIN');
    } else if (activeTab === 'salesmen') {
      return users.filter(user => user.role === 'USER');
    }
    return users;
  }, [users, activeTab]);

  // Count users by role
  const userCounts = useMemo(() => {
    const admins = users.filter(user => user.role === 'ADMIN').length;
    const salesmen = users.filter(user => user.role === 'USER').length;
    return { admins, salesmen, total: users.length };
  }, [users]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const tabs = [
    { id: 'all', label: `All Team (${userCounts.total})`, icon: <UserGroupIcon className="h-5 w-5" /> },
    { id: 'admins', label: `Administrators (${userCounts.admins})`, icon: <ShieldCheckIcon className="h-5 w-5" /> },
    { id: 'salesmen', label: `Salesmen (${userCounts.salesmen})`, icon: <UsersIcon className="h-5 w-5" /> },
    { id: 'teams', label: `Teams (${teams.length})`, icon: <UserGroupIcon className="h-5 w-5" /> },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Enhanced Hero Section */}
      <PageHero
        title="Team Management"
        subtitle="Manage your team members, roles, and permissions. View administrators and salesmen separately."
        icon={<UserGroupIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="cyan"
      >
        <div className="flex flex-wrap items-center gap-3 mt-2">
          {activeTab === 'teams' ? (
            <Button
              variant="primary"
              onClick={() => {
                setTeamFormData({ name: '', description: '', isActive: true });
                setShowCreateTeamForm(true);
              }}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Add Team
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => setShowCreateForm(true)}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              Add Team Member
            </Button>
          )}

          {/* Tabs */}
          <div className="w-full mt-4">
            <Tabs
              tabs={tabs}
              activeTab={activeTab}
              onChange={(tabId) => setActiveTab(tabId as TabType)}
            />
          </div>
        </div>
      </PageHero>

      {/* Success/Error Messages */}
      {error && (
        <Card variant="bordered" className="mb-4 border-error-200 bg-error-50">
          <CardBody className="p-4">
            <div className="text-sm text-error-600">{error}</div>
          </CardBody>
        </Card>
      )}

      {success && (
        <Card variant="bordered" className="mb-4 border-success-200 bg-success-50">
          <CardBody className="p-4">
            <div className="text-sm text-success-600">{success}</div>
          </CardBody>
        </Card>
      )}

      {/* Display Switch based on Tab */}
      {activeTab === 'teams' ? (
        // Teams Grid
        teams.length === 0 ? (
          <Card variant="glass">
            <CardBody>
              <EmptyState
                title="No teams"
                description="Get started by creating a new sales team."
                icon={<UserGroupIcon className="h-12 w-12" />}
                action={
                  <Button
                    variant="primary"
                    onClick={() => setShowCreateTeamForm(true)}
                    leftIcon={<PlusIcon className="h-5 w-5" />}
                  >
                    Add Team
                  </Button>
                }
              />
            </CardBody>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.map((team) => (
              <Card
                key={team.id}
                variant="glass"
                hoverable
                className="group transition-all duration-200 hover:shadow-xl"
              >
                <CardBody className="p-6">
                  {/* Team Info */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0">
                      <div className="h-16 w-16 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-br from-indigo-500 to-purple-600">
                        <UserGroupIcon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-neutral-900 truncate">
                        {team.name}
                      </h3>
                      <p className="text-sm text-neutral-500 truncate">{team.description || 'No description'}</p>
                      <div className="mt-2">
                        <Badge variant={team.isActive ? 'success' : 'neutral'} size="sm">
                          {team.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Team Details (Stats) */}
                  <div className="space-y-2 mb-4 pb-4 border-b border-neutral-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-neutral-500">Members</span>
                      <span className="text-neutral-900 font-medium">
                        {team._count?.users || 0} Salesmen
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-neutral-500">Created</span>
                      <span className="text-neutral-900 font-medium">
                        {new Date(team.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={async () => {
                        try {
                          // Fetch complete team details with members before showing modal
                          const fullTeam = await teamsAPI.getById(team.id);
                          setSelectedTeam({
                            ...team,
                            users: fullTeam.users,
                          });
                          setShowTeamDetails(true);
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      leftIcon={<EyeIcon className="h-4 w-4" />}
                      className="flex-1"
                    >
                      View Members
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditTeam(team)}
                      className="flex-shrink-0"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteTeam(team)}
                      className="text-error-600 hover:text-error-700 hover:bg-error-50 flex-shrink-0"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )
      ) : (
        // Users Grid - Card-based Layout
        filteredUsers.length === 0 ? (
          <Card variant="glass">
            <CardBody>
              <EmptyState
                title={activeTab === 'admins' ? 'No administrators' : activeTab === 'salesmen' ? 'No salesmen' : 'No team members'}
                description={`Get started by adding a new ${activeTab === 'admins' ? 'administrator' : activeTab === 'salesmen' ? 'salesman' : 'team member'}.`}
                icon={<UserIcon className="h-12 w-12" />}
                action={
                  <Button
                    variant="primary"
                    onClick={() => setShowCreateForm(true)}
                    leftIcon={<PlusIcon className="h-5 w-5" />}
                  >
                    Add Team Member
                  </Button>
                }
              />
            </CardBody>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map((user) => (
              <Card
                key={user.id}
                variant="glass"
                hoverable
                clickable
                className="group transition-all duration-200 hover:shadow-xl"
              >
                <CardBody className="p-6">
                  {/* User Avatar and Info */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0">
                      <div className={`h-16 w-16 rounded-xl flex items-center justify-center shadow-lg ${user.role === 'ADMIN'
                        ? 'bg-gradient-to-br from-purple-500 to-indigo-600'
                        : 'bg-gradient-to-br from-blue-500 to-cyan-600'
                        }`}>
                        {user.role === 'ADMIN' ? (
                          <ShieldCheckIcon className="h-8 w-8 text-white" />
                        ) : (
                          <UserIcon className="h-8 w-8 text-white" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-neutral-900 truncate">
                        {user.firstName} {user.lastName}
                      </h3>
                      <p className="text-sm text-neutral-500 truncate">{user.email}</p>
                      <div className="mt-2">
                        <Badge
                          variant={getRoleBadge(user.role) as 'primary' | 'secondary'}
                          size="sm"
                        >
                          {user.role === 'ADMIN' ? 'Administrator' : 'Salesman'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* User Details */}
                  <div className="space-y-2 mb-4 pb-4 border-b border-neutral-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-neutral-500">Status</span>
                      <Badge variant={user.isActive ? 'success' : 'neutral'} size="sm">
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    {user.role === 'USER' && (user as any).preferredLanguage && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-500">Language</span>
                        <span className="text-neutral-900 font-medium">
                          {(user as any).preferredLanguage === 'ARABIC' ? '🇸🇦 Arabic' : '🇬🇧 English'}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-neutral-500">Joined</span>
                      <span className="text-neutral-900 font-medium">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditUser(user)}
                      leftIcon={<PencilIcon className="h-4 w-4" />}
                      className="flex-1"
                    >
                      Edit
                    </Button>
                    {user.role === 'USER' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResetPasswordUser(user)}
                        leftIcon={<LockClosedIcon className="h-4 w-4" />}
                        className="flex-1"
                      >
                        Password
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteUser(user)}
                      className="text-error-600 hover:text-error-700 hover:bg-error-50"
                      title="Delete user"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )
      )}

      {/* Create User Modal - Enhanced UI */}
      <Modal
        isOpen={showCreateForm}
        onClose={() => {
          setShowCreateForm(false);
          setPasswordStrength(null);
        }}
        title="Create New User"
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Header with icon */}
          <div className="flex items-center gap-3 pb-4 border-b border-neutral-200">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg">
              <UserPlusIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">Add New Team Member</h3>
              <p className="text-sm text-neutral-500">Create a new user account with appropriate permissions</p>
            </div>
          </div>

          {/* Account Credentials Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheckIcon className="h-5 w-5 text-primary-600" />
              <h4 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide">Account Credentials</h4>
            </div>

            {/* Email Field */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-2">
                <EnvelopeIcon className="h-4 w-4 text-neutral-400" />
                Email Address
                <span className="text-error-500">*</span>
                <HelpTooltip content="The email address will be used for login and system notifications" />
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="user@example.com"
                className="block w-full px-4 py-2.5 rounded-lg border border-neutral-300 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm placeholder:text-neutral-400"
              />
            </div>

            {/* Password Field */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-2">
                <LockClosedIcon className="h-4 w-4 text-neutral-400" />
                Password
                <span className="text-error-500">*</span>
                <HelpTooltip content="Password must be at least 8 characters long. Use a mix of uppercase, lowercase, numbers, and special characters for better security." />
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter a strong password"
                  className="block w-full px-4 py-2.5 rounded-lg border border-neutral-300 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm pr-10 placeholder:text-neutral-400"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-neutral-50 rounded-r-lg transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
                  )}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {passwordStrength && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-neutral-600">Password Strength:</span>
                    <span className={`text-xs font-semibold ${passwordStrength === 'weak' ? 'text-error-600' :
                      passwordStrength === 'medium' ? 'text-warning-600' :
                        'text-success-600'
                      }`}>
                      {passwordStrength === 'weak' ? 'Weak' : passwordStrength === 'medium' ? 'Medium' : 'Strong'}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <div className={`h-1.5 flex-1 rounded-full ${passwordStrength === 'weak' ? 'bg-error-500' :
                      passwordStrength === 'medium' ? 'bg-warning-500' :
                        'bg-success-500'
                      }`} />
                    <div className={`h-1.5 flex-1 rounded-full ${passwordStrength === 'medium' || passwordStrength === 'strong' ?
                      (passwordStrength === 'medium' ? 'bg-warning-500' : 'bg-success-500') :
                      'bg-neutral-200'
                      }`} />
                    <div className={`h-1.5 flex-1 rounded-full ${passwordStrength === 'strong' ? 'bg-success-500' : 'bg-neutral-200'
                      }`} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Personal Information Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <UserIcon className="h-5 w-5 text-primary-600" />
              <h4 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide">Personal Information</h4>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-2">
                  First Name
                  <HelpTooltip content="User's first name for personalization" />
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="John"
                  className="block w-full px-4 py-2.5 rounded-lg border border-neutral-300 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm placeholder:text-neutral-400"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-2">
                  Last Name
                  <HelpTooltip content="User's last name for personalization" />
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Doe"
                  className="block w-full px-4 py-2.5 rounded-lg border border-neutral-300 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm placeholder:text-neutral-400"
                />
              </div>
            </div>
          </div>

          {/* Role & Permissions Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheckIcon className="h-5 w-5 text-primary-600" />
              <h4 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide">Role & Permissions</h4>
            </div>

            {/* Role Selection */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-2">
                User Role
                <span className="text-error-500">*</span>
                <HelpTooltip content="Admin users have full system access. Regular users (salesmen) have limited access to their assigned clients and deals." />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role: 'USER' }))}
                  className={`relative flex items-start gap-3 p-4 rounded-lg border-2 transition-all ${formData.role === 'USER'
                    ? 'border-primary-500 bg-primary-50 shadow-md'
                    : 'border-neutral-200 bg-white hover:border-neutral-300'
                    }`}
                >
                  <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.role === 'USER' ? 'border-primary-500' : 'border-neutral-300'
                    }`}>
                    {formData.role === 'USER' && (
                      <div className="w-3 h-3 rounded-full bg-primary-500" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <UserIcon className="h-4 w-4 text-neutral-600" />
                      <span className="font-semibold text-sm text-neutral-900">Salesman</span>
                    </div>
                    <p className="text-xs text-neutral-500 mt-1">Regular user with limited access</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role: 'ADMIN' }))}
                  className={`relative flex items-start gap-3 p-4 rounded-lg border-2 transition-all ${formData.role === 'ADMIN'
                    ? 'border-primary-500 bg-primary-50 shadow-md'
                    : 'border-neutral-200 bg-white hover:border-neutral-300'
                    }`}
                >
                  <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.role === 'ADMIN' ? 'border-primary-500' : 'border-neutral-300'
                    }`}>
                    {formData.role === 'ADMIN' && (
                      <div className="w-3 h-3 rounded-full bg-primary-500" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <ShieldCheckIcon className="h-4 w-4 text-neutral-600" />
                      <span className="font-semibold text-sm text-neutral-900">Administrator</span>
                    </div>
                    <p className="text-xs text-neutral-500 mt-1">Full system access and control</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Team Association - Only for Users */}
            {formData.role === 'USER' && (
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-2">
                  <UserGroupIcon className="h-4 w-4 text-neutral-400" />
                  Assign to Team (Optional)
                </label>
                <select
                  name="teamId"
                  value={formData.teamId}
                  onChange={handleChange}
                  className="block w-full px-4 py-2.5 rounded-lg border border-neutral-300 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm"
                >
                  <option value="">No Team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Language Preference - Only for Users */}
            {formData.role === 'USER' && (
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 mb-2">
                  <LanguageIcon className="h-4 w-4 text-neutral-400" />
                  Preferred Language
                  <span className="text-error-500">*</span>
                  <HelpTooltip content="The language used for the user interface and communications" />
                </label>
                <select
                  name="preferredLanguage"
                  required
                  value={formData.preferredLanguage}
                  onChange={handleChange}
                  className="block w-full px-4 py-2.5 rounded-lg border border-neutral-300 shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all sm:text-sm"
                >
                  <option value="ARABIC">🇸🇦 Arabic</option>
                  <option value="ENGLISH">🇬🇧 English</option>
                </select>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t border-neutral-200">
            <Button
              variant="ghost"
              onClick={() => {
                setShowCreateForm(false);
                setPasswordStrength(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              leftIcon={<CheckCircleIcon className="h-5 w-5" />}
            >
              Create User
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={showEditForm && !!selectedUser}
        onClose={() => {
          setShowEditForm(false);
          setSelectedUser(null);
        }}
        title="Edit User"
        size="md"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700">Email *</label>
            <input
              type="email"
              name="email"
              required
              value={editFormData.email}
              onChange={handleEditChange}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">First Name</label>
            <input
              type="text"
              name="firstName"
              value={editFormData.firstName}
              onChange={handleEditChange}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">Last Name</label>
            <input
              type="text"
              name="lastName"
              value={editFormData.lastName}
              onChange={handleEditChange}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">Role *</label>
            <select
              name="role"
              required
              value={editFormData.role}
              onChange={handleEditChange}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="USER">User (Salesman)</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          {editFormData.role === 'USER' && (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-700">Preferred Language *</label>
                <select
                  name="preferredLanguage"
                  required
                  value={editFormData.preferredLanguage}
                  onChange={handleEditChange}
                  className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="ARABIC">Arabic</option>
                  <option value="ENGLISH">English</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700">Team (Optional)</label>
                <select
                  name="teamId"
                  value={editFormData.teamId}
                  onChange={handleEditChange}
                  className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="">No Team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isActive"
              checked={editFormData.isActive}
              onChange={handleEditChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
            />
            <label className="ml-2 block text-sm text-neutral-900">
              Active User
            </label>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setShowEditForm(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
            >
              Update User
            </Button>
          </div>
        </form>
      </Modal>

      {/* Reset Salesman Password Modal */}
      <Modal
        isOpen={showResetPasswordForm && !!selectedUser}
        onClose={closeResetPasswordForm}
        title="Reset Salesman Password"
        size="md"
      >
        <form onSubmit={handleResetPasswordSubmit} className="space-y-5">
          <div className="flex items-start gap-3 rounded-lg border border-warning-200 bg-warning-50 p-4">
            <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-warning-100">
              <LockClosedIcon className="h-5 w-5 text-warning-700" />
            </div>
            <div>
              <p className="text-sm font-semibold text-neutral-900">
                {selectedUser?.firstName || selectedUser?.email}
              </p>
              <p className="mt-1 text-sm text-neutral-600">
                Set a new password for this salesman. They can use it immediately on their next login.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">New Password *</label>
            <div className="relative mt-1">
              <input
                type={showResetPassword ? 'text' : 'password'}
                name="password"
                required
                minLength={8}
                value={resetPasswordFormData.password}
                onChange={handleResetPasswordChange}
                className="block w-full rounded-lg border-neutral-300 pr-11 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Enter a new password"
              />
              <button
                type="button"
                onClick={() => setShowResetPassword(prev => !prev)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-neutral-400 hover:text-neutral-600"
                title={showResetPassword ? 'Hide password' : 'Show password'}
              >
                {showResetPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {resetPasswordStrength && (
              <div className="mt-2">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs text-neutral-500">Password strength</span>
                  <span className={`text-xs font-semibold ${resetPasswordStrength === 'weak' ? 'text-error-600' :
                    resetPasswordStrength === 'medium' ? 'text-warning-600' :
                      'text-success-600'
                    }`}>
                    {resetPasswordStrength === 'weak' ? 'Weak' : resetPasswordStrength === 'medium' ? 'Medium' : 'Strong'}
                  </span>
                </div>
                <div className="flex gap-1">
                  <div className={`h-1.5 flex-1 rounded-full ${resetPasswordStrength === 'weak' ? 'bg-error-500' :
                    resetPasswordStrength === 'medium' ? 'bg-warning-500' :
                      'bg-success-500'
                    }`} />
                  <div className={`h-1.5 flex-1 rounded-full ${resetPasswordStrength === 'medium' || resetPasswordStrength === 'strong' ?
                    (resetPasswordStrength === 'medium' ? 'bg-warning-500' : 'bg-success-500') :
                    'bg-neutral-200'
                    }`} />
                  <div className={`h-1.5 flex-1 rounded-full ${resetPasswordStrength === 'strong' ? 'bg-success-500' : 'bg-neutral-200'
                    }`} />
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">Confirm Password *</label>
            <input
              type={showResetPassword ? 'text' : 'password'}
              name="confirmPassword"
              required
              minLength={8}
              value={resetPasswordFormData.confirmPassword}
              onChange={handleResetPasswordChange}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Re-enter the new password"
            />
            {resetPasswordFormData.confirmPassword && resetPasswordFormData.password !== resetPasswordFormData.confirmPassword && (
              <p className="mt-2 text-xs text-error-600">Passwords do not match.</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
            <Button
              variant="ghost"
              onClick={closeResetPasswordForm}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              leftIcon={<LockClosedIcon className="h-5 w-5" />}
              disabled={
                resetPasswordFormData.password.length < 8 ||
                resetPasswordFormData.password !== resetPasswordFormData.confirmPassword
              }
            >
              Reset Password
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete User/Team Confirmation Dialogs */}
      <Modal
        isOpen={showDeleteDialog && !!selectedUser}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedUser(null);
        }}
        title="Delete User"
        size="sm"
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-error-100">
            <TrashIcon className="h-6 w-6 text-error-600" />
          </div>
          <div className="mt-4">
            <p className="text-sm text-neutral-500">
              Are you sure you want to delete <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong> ({selectedUser?.email})?
            </p>
            <p className="text-sm text-neutral-500 mt-2">
              This action cannot be undone. If the user has associated data, they will be deactivated instead of deleted.
            </p>
          </div>
          <div className="flex justify-center space-x-3 pt-6">
            <Button
              variant="ghost"
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteUser}
            >
              Delete User
            </Button>
          </div>
        </div>
      </Modal>

      {/* --- TEAMS MODALS --- */}

      {/* Create Team Modal */}
      <Modal
        isOpen={showCreateTeamForm}
        onClose={() => setShowCreateTeamForm(false)}
        title="Create Team"
        size="md"
      >
        <form onSubmit={handleCreateTeamSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700">Team Name *</label>
            <input
              type="text"
              required
              value={teamFormData.name}
              onChange={(e) => setTeamFormData({ ...teamFormData, name: e.target.value })}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">Description</label>
            <textarea
              value={teamFormData.description}
              onChange={(e) => setTeamFormData({ ...teamFormData, description: e.target.value })}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="ghost" onClick={() => setShowCreateTeamForm(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Create Team</Button>
          </div>
        </form>
      </Modal>

      {/* Edit Team Modal */}
      <Modal
        isOpen={showEditTeamForm && !!selectedTeam}
        onClose={() => {
          setShowEditTeamForm(false);
          setSelectedTeam(null);
        }}
        title="Edit Team"
        size="md"
      >
        <form onSubmit={handleEditTeamSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700">Team Name *</label>
            <input
              type="text"
              required
              value={teamFormData.name}
              onChange={(e) => setTeamFormData({ ...teamFormData, name: e.target.value })}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">Description</label>
            <textarea
              value={teamFormData.description}
              onChange={(e) => setTeamFormData({ ...teamFormData, description: e.target.value })}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={teamFormData.isActive}
              onChange={(e) => setTeamFormData({ ...teamFormData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
            />
            <label className="ml-2 block text-sm text-neutral-900">Active Team</label>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="ghost" onClick={() => setShowEditTeamForm(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Update Team</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Team Dialog */}
      <Modal
        isOpen={showDeleteTeamDialog && !!selectedTeam}
        onClose={() => {
          setShowDeleteTeamDialog(false);
          setSelectedTeam(null);
        }}
        title="Delete Team"
        size="sm"
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-error-100">
            <TrashIcon className="h-6 w-6 text-error-600" />
          </div>
          <div className="mt-4">
            <p className="text-sm text-neutral-500">
              Are you sure you want to delete <strong>{selectedTeam?.name}</strong>?
            </p>
            <p className="text-sm text-error-500 mt-2 font-medium">
              This will remove all salesmen from this team.
            </p>
          </div>
          <div className="flex justify-center space-x-3 pt-6">
            <Button variant="ghost" onClick={() => setShowDeleteTeamDialog(false)}>Cancel</Button>
            <Button variant="danger" onClick={confirmDeleteTeam}>Delete Team</Button>
          </div>
        </div>
      </Modal>

      {/* Team Details Modal (Managing Members) */}
      <Modal
        isOpen={showTeamDetails && !!selectedTeam}
        onClose={() => {
          setShowTeamDetails(false);
          setSelectedTeam(null);
        }}
        title={`Team Members: ${selectedTeam?.name}`}
        size="lg"
      >
        <div className="space-y-6">
          <div className="bg-neutral-50 p-4 rounded-lg flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Add Salesman to Team</label>
              <select
                className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                onChange={(e) => {
                  if (e.target.value) {
                    assignUserToTeam(Number(e.target.value), selectedTeam!.id);
                    e.target.value = '';
                  }
                }}
                defaultValue=""
              >
                <option value="" disabled>Select a salesman...</option>
                {users
                  .filter(u => u.role === 'USER' && (!selectedTeam?.users?.find(tu => tu.id === u.id)))
                  .map(user => (
                    <option key={user.id} value={user.id}>{user.firstName} {user.lastName} ({user.email})</option>
                  ))}
              </select>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-neutral-900 mb-4 border-b pb-2">Current Members ({selectedTeam?.users?.length || 0})</h4>
            {selectedTeam?.users && selectedTeam.users.length > 0 ? (
              <ul className="divide-y divide-neutral-200 border rounded-lg overflow-hidden">
                {selectedTeam.users.map(user => (
                  <li key={user.id} className="flex items-center justify-between p-3 hover:bg-neutral-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <UserIcon className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-neutral-900">{user.firstName} {user.lastName}</p>
                        <p className="text-xs text-neutral-500">{user.email}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeUserFromTeam(user.id, selectedTeam.id)}
                      className="text-error-600 hover:text-error-700"
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-8 text-neutral-500 border border-dashed rounded-lg">
                No members in this team yet.
              </div>
            )}
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default UserManagement;
